/**
 * CategoryFilter - Horizontal chip/button row for filtering by category
 *
 * Used in the Comparison page to filter compared products by category.
 */

interface CategoryFilterProps {
  /** Available categories with counts */
  categories: Array<{
    id: string;
    name: string;
    count: number;
  }>;
  /** Currently selected category (null = All) */
  selectedCategory: string | null;
  /** Handler when category is selected */
  onSelectCategory: (categoryId: string | null) => void;
}

export function CategoryFilter({
  categories,
  selectedCategory,
  onSelectCategory,
}: CategoryFilterProps) {
  // Calculate total count for "All" option
  const totalCount = categories.reduce((sum, cat) => sum + cat.count, 0);

  // Sort categories by count descending, then alphabetically
  const sortedCategories = [...categories].sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by category">
      {/* All option */}
      <button
        onClick={() => onSelectCategory(null)}
        className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
          selectedCategory === null
            ? 'bg-orange-500 text-black'
            : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700 hover:text-white'
        }`}
        aria-pressed={selectedCategory === null}
      >
        All
        <span className="ml-1.5 text-xs opacity-70">({totalCount})</span>
      </button>

      {/* Category chips */}
      {sortedCategories.map((category) => (
        <button
          key={category.id}
          onClick={() => onSelectCategory(category.id)}
          className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
            selectedCategory === category.id
              ? 'bg-orange-500 text-black'
              : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700 hover:text-white'
          }`}
          aria-pressed={selectedCategory === category.id}
        >
          {category.name}
          <span className="ml-1.5 text-xs opacity-70">({category.count})</span>
        </button>
      ))}
    </div>
  );
}

